import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Loader2, LogOut, Mail, Phone, Briefcase, CalendarClock } from "lucide-react";
import Logo from "@/components/Logo";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "contacted", label: "Contacted" },
  { value: "archived", label: "Archived" },
];

const statusStyle = {
  new: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  contacted: "bg-emerald-100 text-emerald-700",
  archived: "bg-slate-200 text-slate-600",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("unauthenticated");
        return res.json();
      })
      .then(setUser)
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enquiries`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enquiries/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
  };

  const logout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    navigate("/admin/login");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return iso;
    }
  };

  if (checking || (user && loading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1F3A]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" data-testid="admin-loading-spinner" />
      </div>
    );
  }
  if (!user && !checking) return null;

  const newCount = items.filter((e) => e.status === "new").length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* topbar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B1F3A]">
        <div className="container-x flex h-16 items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-300 sm:block" data-testid="admin-welcome-text">
              Signed in as <span className="font-semibold text-white">{user?.email}</span>
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-red-400 hover:text-red-300"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-10">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Enquiries</h1>
            <p className="mt-1 text-sm text-slate-500">Messages submitted through the website contact form.</p>
          </div>
          <div className="flex gap-3">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="admin-total-enquiries">
              <p className="font-mono-alt text-xl font-bold text-[#0B1F3A]">{items.length}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="admin-new-enquiries">
              <p className="font-mono-alt text-xl font-bold text-blue-600">{newCount}</p>
              <p className="text-xs text-slate-500">New</p>
            </div>
          </div>
        </div>

        {items.length === 0 && !loading ? (
          <div className="card-soft mt-8 flex flex-col items-center justify-center p-14 text-center" data-testid="admin-empty-state">
            <Inbox className="h-10 w-10 text-slate-300" aria-hidden="true" />
            <p className="mt-4 font-display font-semibold text-[#0B1F3A]">No enquiries yet</p>
            <p className="mt-1 text-sm text-slate-500">New website enquiries will appear here automatically.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {items.map((enq) => (
              <article key={enq.id} className="card-soft p-6" data-testid="admin-enquiry-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-base font-semibold text-[#0B1F3A]" data-testid="admin-enquiry-name">{enq.name}</h2>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500" data-testid="admin-enquiry-date">
                      <CalendarClock className="h-3.5 w-3.5" /> {fmt(enq.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[enq.status] || statusStyle.new}`} data-testid="admin-enquiry-status-badge">
                      {(STATUSES.find((s) => s.value === enq.status) || STATUSES[0]).label}
                    </span>
                    <select
                      value={enq.status}
                      onChange={(e) => setStatus(enq.id, e.target.value)}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 focus:border-blue-600 focus:outline-none"
                      aria-label="Update enquiry status"
                      data-testid={`admin-enquiry-status-select-${enq.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                  <a href={`mailto:${enq.email}`} className="inline-flex items-center gap-1.5 transition-colors hover:text-blue-600" data-testid="admin-enquiry-email">
                    <Mail className="h-4 w-4 text-blue-600" /> {enq.email}
                  </a>
                  <a href={`tel:${enq.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 transition-colors hover:text-blue-600" data-testid="admin-enquiry-phone">
                    <Phone className="h-4 w-4 text-blue-600" /> {enq.phone}
                  </a>
                  {enq.company && (
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase className="h-4 w-4 text-blue-600" /> {enq.company}
                    </span>
                  )}
                </div>

                <p className="mt-3 inline-flex rounded-md bg-blue-600/10 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid="admin-enquiry-service">
                  {enq.service}
                </p>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600" data-testid="admin-enquiry-message">
                  {enq.message}
                </p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
